import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { StudentLayout } from "@/components/Layouts";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { DashboardCard } from "@/components/DashboardCard";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { Video, BookOpen, FileText, CalendarRange, GraduationCap, ArrowRight, Megaphone, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

function AccessGate({ user, children }) {
  if (user.access_status === "pending") {
    return (
      <Card className="glass-card border-gold/40">
        <CardContent className="p-6 sm:p-8 text-center" data-testid="access-pending-warning">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gold/15 border border-gold/30 mb-3">
            <CheckCircle2 className="h-5 w-5 text-gold" />
          </div>
          <h2 className="text-lg font-heading font-semibold">Access Pending</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Your access is pending approval. Please contact the Maahad Bursa admin.
          </p>
        </CardContent>
      </Card>
    );
  }
  if (user.access_status === "expired") {
    return (
      <Card className="glass-card border-destructive/40">
        <CardContent className="p-6 sm:p-8 text-center" data-testid="access-expired-warning">
          <h2 className="text-lg font-heading font-semibold">Access Expired</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Your access has expired. Please contact admin to renew access.
          </p>
        </CardContent>
      </Card>
    );
  }
  return children;
}

export default function StudentDashboard() {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [modules, setModules] = useState([]);
  const [notes, setNotes] = useState([]);
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    if (user?.access_status !== "active") return;
    Promise.all([
      api.get("/progress").then((r) => setProgress(r.data)).catch(() => {}),
      api.get("/recordings").then((r) => setRecordings(r.data)).catch(() => {}),
      api.get("/modules").then((r) => setModules(r.data)).catch(() => {}),
      api.get("/notes").then((r) => setNotes(r.data)).catch(() => {}),
      api.get("/events").then((r) => setEvents(r.data)).catch(() => {}),
      api.get("/announcements").then((r) => setAnnouncements(r.data.slice(0, 3))).catch(() => {}),
    ]);
  }, [user?.access_status]);

  if (!user) return null;

  const recordingsAvailable = recordings.filter((r) => r.status === "tersedia").length;
  const modulesCompleted = modules.filter((m) => m.completed).length;
  const notesAvailable = notes.filter((n) => n.status === "tersedia").length;
  const upcomingEvents = events.filter((e) => e.event_date >= new Date().toISOString().slice(0, 10));
  const nextEvent = upcomingEvents[0];

  return (
    <StudentLayout title="Dashboard">
      <div className="space-y-6">
        <AccessGate user={user}>
          {/* Welcome */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-primary font-semibold">Welcome</p>
              <h1 className="mt-1 text-3xl sm:text-4xl font-heading font-bold tracking-tight">
                {user.name}
              </h1>
              <p className="mt-2 text-muted-foreground">
                Continue your stock learning journey.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <StatusBadge status={user.access_status} />
              <span className="status-pill bg-card border-border text-muted-foreground">
                {user.batch || "—"}
              </span>
            </div>
          </div>

          {/* Course access card */}
          <Card className="glass-card relative overflow-hidden" data-testid="course-access-card">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-secondary/10" />
            <CardContent className="p-6 sm:p-8 relative">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div>
                  <p className="text-xs uppercase tracking-widest text-primary font-semibold">Course Access</p>
                  <h2 className="mt-2 text-2xl sm:text-3xl font-heading font-bold tracking-tight">
                    {user.course || "Beginner Stock Investment Class"}
                  </h2>
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="font-medium truncate">{user.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Batch</p>
                      <p className="font-medium">{user.batch || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Status</p>
                      <p className="font-medium capitalize">{user.access_status}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Expired</p>
                      <p className="font-medium">{user.access_expiry || "—"}</p>
                    </div>
                  </div>
                </div>
                <div className="lg:min-w-[280px]">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium">Overall Progress</p>
                    <span className="text-2xl font-heading font-bold text-primary">{progress?.overall_progress ?? user.progress ?? 0}%</span>
                  </div>
                  <Progress value={progress?.overall_progress ?? user.progress ?? 0} className="h-2" />
                  <Button asChild className="w-full mt-4 btn-glow" data-testid="continue-learning-btn">
                    <Link to="/student/course">
                      Continue Learning <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <DashboardCard
              icon3d="video"
              icon={Video}
              label="Recordings Available"
              value={`${recordingsAvailable}/${recordings.length || 2}`}
              sublabel="Class sessions"
              accent="teal"
              testId="stat-recordings"
            />
            <DashboardCard
              icon3d="book"
              icon={BookOpen}
              label="Module Completed"
              value={`${modulesCompleted}/${modules.length || 6}`}
              sublabel="Out of 6 modules"
              accent="navy"
              testId="stat-modules"
            />
            <DashboardCard
              icon3d="document"
              icon={FileText}
              label="Notes Available"
              value={`${notesAvailable}/${notes.length || 6}`}
              sublabel="PDF downloads"
              accent="gold"
              testId="stat-notes"
            />
            <DashboardCard
              icon3d="calendar"
              icon={CalendarRange}
              label="Upcoming Events"
              value={upcomingEvents.length}
              sublabel={nextEvent ? `Next: ${nextEvent.event_date}` : "No events"}
              accent="teal"
              testId="stat-events"
            />
          </div>

          {/* Latest announcements */}
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
                  <Megaphone className="h-4.5 w-4.5 text-primary" /> Latest Announcements
                </h3>
                <Button asChild variant="ghost" size="sm" data-testid="view-all-announcements">
                  <Link to="/student/announcements">View All <ArrowRight className="h-3.5 w-3.5" /></Link>
                </Button>
              </div>
              {announcements.length === 0 ? (
                <EmptyState icon={Megaphone} title="No announcements" description="There are no announcements at this time." testId="announcements-empty" />
              ) : (
                <ul className="space-y-3">
                  {announcements.map((a) => (
                    <li key={a.id} className="rounded-xl bg-muted/40 border border-border p-4" data-testid={`announcement-${a.id}`}>
                      <h4 className="font-semibold">{a.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{a.message}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </AccessGate>
      </div>
    </StudentLayout>
  );
}
